import { ResponsiveContainer, Sankey } from "recharts";
import { SankeyNode } from "recharts/types/util/types";
import { EmissionsSourceSummary } from "../api/models/EmissionsSource";
import { formatNumber } from "../utils/formatNumber";

const groupBy = <T,>(list: T[], getKey: (item: T) => string) =>
    list.reduce((previous, currentItem) => {
        const group = getKey(currentItem) as string;
        if (!previous[group]) previous[group] = [];
        previous[group].push(currentItem);
        return previous;
    }, {} as Record<string, T[]>);

type GroupedByTableSummary = {
    id: string;
    name: string;
    portfolioName: string;
    sectorName: string;
    financedEmissionsInPeriod: number;
};

export const EmissionsSankeyGraph = ({
    emissionsSources,
}: {
    emissionsSources: EmissionsSourceSummary[];
}) => {
    const nodes: { name: string }[] = [];

    const links: { source: number; target: number; value: number }[] = [];

    const groupedByAssets = groupBy(emissionsSources, (es) =>
        es.uniqueReference.split("-")[0].toLowerCase()
    );

    const assetsByOrder = Object.entries(groupedByAssets)
        .map(([id, assets]) => {
            const merge = assets.reduce((a, b) => ({
                ...a,
                ...b,
                financedEmissionsInPeriod:
                    a.financedEmissionsInPeriod + b.financedEmissionsInPeriod,
            }));
            return {
                ...merge,
                id,
            } as GroupedByTableSummary;
        })
        .filter((asset) => asset.financedEmissionsInPeriod > 0);

    const assetsGroupedBySector = groupBy(assetsByOrder, (es) => es.sectorName);

    nodes.push({ name: "Total" });
    Object.entries(assetsGroupedBySector).forEach(([k, v]) => {
        nodes.push({ name: k });
        links.push({
            target: nodes.length - 1,
            source: 0,
            value: v.reduce(
                (count, summary) => count + summary.financedEmissionsInPeriod,
                0
            ),
        });
        const target = nodes.length - 1;
        v.forEach((summary) => {
            nodes.push({ name: summary.name });
            links.push({
                target: nodes.length - 1,
                source: target,
                value: summary.financedEmissionsInPeriod,
            });
        });
    });

    const data = { nodes, links };

    return (
        <div className="h-[400px]">
            <ResponsiveContainer width={"100%"} height={"100%"}>
                <Sankey
                    data={data}
                    link={CustomLink}
                    node={CustomNode}
                ></Sankey>
            </ResponsiveContainer>
        </div>
    );
};

const colors = ["#3C898E", "#486DF0", "#6F50E5"];

type CustomLinkPayload = {
    source: CustomNodePayload;
    target: CustomNodePayload;
    value: number;
    dy: number;
    sy: number;
    ty: number;
};
const CustomLink = (props: {
    sourceX: number;
    targetX: number;
    sourceY: number;
    targetY: number;
    sourceControlX: number;
    targetControlX: number;
    sourceRelativeY: number;
    targetRelativeY: number;
    linkWidth: number;
    index: number;
    payload: CustomLinkPayload;
}) => {
    return (
        <g>
            <path
                d={`
M${props.sourceX},${props.sourceY}
C${props.sourceControlX},${props.sourceY} ${props.targetControlX},${props.targetY} ${props.targetX},${props.targetY}`}
                fill="none"
                stroke={colors[props.payload.source.depth % colors.length]}
                strokeOpacity={0.4}
                strokeWidth={props.linkWidth}
                strokeLinecap="butt"
            />
            <foreignObject
                x={props.sourceX}
                y={props.targetY - props.linkWidth / 2}
                width={
                    Math.max(props.targetX, props.sourceX) -
                    Math.min(props.targetX, props.sourceX)
                }
                height={props.linkWidth}
                style={{
                    overflow: "visible",
                }}
            >
                <div
                    style={{
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        width: "100%",
                        height: "100%",
                        overflow: "visible",
                        padding: "0.5em",
                        gap: 8,
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            fontFamily: "sans-serif",
                            textAlign: "center",
                            backgroundColor: "#f1f5fe80",
                            padding: "0.25em 0.5em",
                            borderRadius: 4,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        {props.payload.target.name
                            ? `${props.payload.target.name}: `
                            : ""}
                        {formatNumber(props.payload.value)}
                    </div>
                </div>
            </foreignObject>
        </g>
    );
};

type CustomNodePayload = {
    name: string;
    sourceNodes: number[];
    sourceLinks: number[];
    targetLinks: number[];
    targetNodes: number[];
    value: number;
    depth: number;
    x: number;
    dx: number;
    y: number;
    dy: number;
};
const CustomNode = (
    props: SankeyNode & {
        width: number;
        height: number;
        payload: CustomNodePayload;
    }
): React.ReactElement => {
    return (
        <rect
            x={props.x + 4}
            y={props.y - 2}
            width={props.width}
            height={props.height + 4}
            fill={colors[props.payload.depth % colors.length]}
            rx={2.5}
            textAnchor={props.value?.toString()}
        />
    );
};
